/**
 * Minimal browser-safe JWT decoder. We only inspect the payload claims for
 * UI gating — signature verification is the backend's job.
 *
 * Returns `null` on any decoding failure so callers can fall back to
 * whatever was provided in the login response body.
 */
export interface JwtClaims {
  sub?: string;
  tenant_id?: string;
  sid?: string;
  name?: string;
  role?: string;
  email?: string;
  permissions?: string[] | string;
  exp?: number;
  iat?: number;
  [k: string]: unknown;
}

function base64UrlDecode(input: string): string {
  const pad = input.length % 4 === 0 ? '' : '='.repeat(4 - (input.length % 4));
  const b64 = (input + pad).replace(/-/g, '+').replace(/_/g, '/');
  if (typeof atob === 'function') {
    return decodeURIComponent(
      atob(b64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  }
  // Node fallback
  return Buffer.from(b64, 'base64').toString('utf-8');
}

export function decodeJwt(token: string): JwtClaims | null {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1])) as JwtClaims;
    return payload;
  } catch {
    return null;
  }
}

/**
 * Extract permission codes from a JWT. Backends use either an array claim
 * (`permissions: string[]`) or a comma/space-separated scope-style string.
 * Both shapes resolve to a deduplicated, lowercased array.
 */
export function extractPermissions(claims: JwtClaims | null): string[] {
  if (!claims) return [];
  const raw = claims.permissions;
  if (Array.isArray(raw)) {
    return Array.from(new Set(raw.map((p) => String(p).toLowerCase()).filter(Boolean)));
  }
  if (typeof raw === 'string') {
    return Array.from(
      new Set(
        raw
          .split(/[\s,]+/)
          .map((p) => p.trim().toLowerCase())
          .filter(Boolean)
      )
    );
  }
  return [];
}
